<script setup lang="ts">
import { useMediaQuery } from '@vueuse/core'
import { onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import KITLogo from '@/components/KITLogo.vue'
import SemesterSelector from '@/components/SemesterSelector.vue'
import { useAppToast } from '@/composables/useAppToast'

import AdminModeToggle from './AdminModeToggle.vue'
import AppConfig from './AppConfig.vue'
import LanguageSelect from './LanguageSelect.vue'
import ThemeModeToggle from './ThemeModeToggle.vue'
import UserAvatar from './UserAvatar.vue'

type PopoverRef = {
  toggle: (event: Event) => void
}

const menu = ref<PopoverRef | null>(null)
const { userInfo, isAuthenticated, fullName, logout: userLogout, clearUserInfo } = useUser()
const { t } = useI18n()
const router = useRouter()
const toast = useAppToast()
const { toggleMenu } = useLayout()
const { initializeMetadata } = useMetadata()
const { canUseAdminMode } = useAdminMode()

// On phones the theme switch, language and color picker fold into one menu so the semester,
// admin and sign-in controls keep their room in the bar.
const isWideTopbar = useMediaQuery('(min-width: 768px)')

onMounted(() => {
  initializeMetadata()
})

async function logout() {
  try {
    const result = await userLogout()
    if (result) {
      clearUserInfo()
      toast.add({
        color: 'success',
        title: t('common.labels.logout'),
        description: t('common.messages.logoutSuccess'),
        duration: 3000,
      })

      router.push('/')
    } else {
      console.error('Logout failed')
    }
  } catch (error) {
    console.error('Error during logout:', error)
  }
}

function toggleUserMenu(event: Event) {
  menu.value?.toggle(event)
}
</script>

<template>
  <header
    class="fixed top-0 left-0 z-90 flex h-[var(--topbar-h)] w-screen items-center justify-between bg-neutral-50 p-0 shadow-[0_0_5px_rgba(64,64,64,0.5)] transition-[height_0.3s,padding_0.3s,transform_0.6s] dark:bg-neutral-950"
  >
    <div
      class="flex h-full w-full items-center justify-between gap-2 px-2 md:gap-8 md:px-4 lg:px-6"
    >
      <div class="flex items-center gap-2 md:gap-4">
        <button class="topbar-item layout-menu-button" aria-label="Menu" @click="toggleMenu">
          <UIcon name="i-lucide-menu" />
        </button>
        <KITLogo class="hidden h-7 w-auto md:block md:h-8 xl:h-9" />
      </div>

      <div class="flex items-center gap-0.5 md:gap-2">
        <template v-if="isWideTopbar">
          <ThemeModeToggle />

          <div class="relative">
            <UPopover :content="{ align: 'end', side: 'bottom', sideOffset: 8 }">
              <button class="topbar-item" aria-label="Settings">
                <UIcon name="i-lucide-palette" />
              </button>
              <template #content>
                <AppConfig />
              </template>
            </UPopover>
          </div>

          <SemesterSelector />

          <LanguageSelect />
        </template>

        <template v-else>
          <SemesterSelector />

          <UPopover :content="{ align: 'end', side: 'bottom', sideOffset: 8 }">
            <button
              type="button"
              class="topbar-item"
              :aria-label="t('pages.user.settings.appearance.title')"
            >
              <UIcon name="i-lucide-settings-2" />
            </button>
            <template #content>
              <div class="w-64 max-w-[calc(100vw-2rem)]">
                <div class="flex flex-col gap-3 p-4 pb-3">
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-sm font-semibold text-muted">
                      {{ t('pages.user.settings.appearance.mode') }}
                    </span>
                    <ThemeModeToggle />
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-sm font-semibold text-muted">
                      {{ t('pages.user.settings.appearance.language') }}
                    </span>
                    <LanguageSelect />
                  </div>
                </div>
                <AppConfig class="w-full! rounded-t-none! border-t border-default" />
              </div>
            </template>
          </UPopover>
        </template>

        <AdminModeToggle v-if="canUseAdminMode" />

        <router-link
          v-if="!isAuthenticated"
          to="/user/login"
          class="topbar-item w-auto! bg-primary! text-inverted!"
          :aria-label="t('common.labels.signIn')"
        >
          <UIcon name="i-lucide-log-in" />
          <span class="ml-2 hidden md:inline-block">
            {{ t('common.labels.signIn') }}
          </span>
        </router-link>

        <template v-else>
          <button
            type="button"
            class="topbar-item rounded-full! p-0!"
            :aria-label="fullName"
            :title="fullName"
            @click="toggleUserMenu"
          >
            <UserAvatar size="sm" />
          </button>
          <AppPopover ref="menu">
            <div
              class="relative flex w-full items-center gap-4 overflow-hidden border-0 bg-transparent p-3"
            >
              <UserAvatar size="xlarge" class="mb-2" />
              <div>
                <div class="font-bold">
                  {{ fullName }}
                </div>
                <div class="text-sm">
                  {{ t(`enums.userType.${userInfo!.userType}`) }}
                </div>
                <div class="text-sm">
                  {{ userInfo!.userName }}
                </div>
              </div>
            </div>
            <div
              class="flex items-center justify-between gap-15 bg-neutral-100 p-2 dark:bg-neutral-800"
            >
              <router-link to="/user/settings">
                <UButton
                  :label="t('router.pages.user-settings')"
                  icon="i-lucide-settings"
                  color="neutral"
                  variant="outline"
                  size="sm"
                />
              </router-link>
              <UButton
                :label="t('common.labels.logout')"
                icon="i-lucide-log-out"
                color="error"
                size="sm"
                @click="logout"
                variant="outline"
              />
            </div>
          </AppPopover>
        </template>
      </div>
    </div>
  </header>
</template>
